/* ==========================================================================
   RD - motion layer
   Scroll reveals + counting numerals. Deliberately small and restrained.

   Two rules baked in:
   1. Content is NEVER gated behind animation. Elements start visible if JS
      fails; the observer only *adds* the pre-reveal state once it can also
      guarantee it will remove it.
   2. A running transform becomes the containing block for absolutely
      positioned descendants. Every revealed element gets `.rd-is-settled`
      after its transition ends, which drops the transform entirely.
   ========================================================================== */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- reveal */

  function settle(el) {
    el.classList.add('rd-is-settled');
  }

  function revealNow(el) {
    el.classList.add('rd-is-in');
    // Drop the transform once the transition finishes so any absolutely
    // positioned descendant resolves against its real ancestor again.
    var done = function (e) {
      if (e && e.target !== el) return;
      settle(el);
      el.removeEventListener('transitionend', done);
    };
    el.addEventListener('transitionend', done);
    // Fallback in case transitionend never fires (display:none, no transition)
    setTimeout(function () {
      settle(el);
    }, 1200);
  }

  function initReveals(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('[data-rd-reveal]:not([data-rd-observed])');
    if (!els.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (el) {
        el.setAttribute('data-rd-observed', '');
        el.classList.add('rd-is-in', 'rd-is-settled');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;

          // Stagger children of a group, capped so a long list never crawls.
          var group = el.closest('[data-rd-stagger]');
          if (group && !el.style.getPropertyValue('--rd-reveal-delay')) {
            var sibs = group.querySelectorAll('[data-rd-reveal]');
            var i = Array.prototype.indexOf.call(sibs, el);
            var step = parseInt(group.getAttribute('data-rd-stagger'), 10) || 70;
            el.style.setProperty('--rd-reveal-delay', Math.min(i, 8) * step + 'ms');
          }

          revealNow(el);
          obs.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    Array.prototype.forEach.call(els, function (el) {
      el.setAttribute('data-rd-observed', '');
      io.observe(el);
    });
  }

  /* --------------------------------------------------------------- counters */
  /* Counts a numeral up to its final value. The final value is written in the
     markup, so the number is correct with JS off - this only animates it. */

  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-rd-count'));
    if (isNaN(target)) return;
    var decimals = parseInt(el.getAttribute('data-rd-decimals'), 10) || 0;
    var prefix = el.getAttribute('data-rd-prefix') || '';
    var suffix = el.getAttribute('data-rd-suffix') || '';
    var dur = 1100;
    var start = null;

    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var v = target * easeOut(p);
      el.textContent = prefix + v.toFixed(decimals) + suffix;
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function initCounters(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('[data-rd-count]:not([data-rd-counted])');
    if (!els.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) return; // leave static value

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-rd-counted', '');
          countUp(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.6 }
    );

    Array.prototype.forEach.call(els, function (el) {
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------------- marquee */
  /* Duplicates track content so the loop is seamless. Pauses on hover/focus. */

  function initMarquee(root) {
    var scope = root || document;
    var tracks = scope.querySelectorAll('[data-rd-marquee]:not([data-rd-marquee-ready])');
    Array.prototype.forEach.call(tracks, function (track) {
      track.setAttribute('data-rd-marquee-ready', '');
      if (reduced.matches) return;
      var inner = track.firstElementChild;
      if (!inner) return;
      var clone = inner.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
      track.classList.add('rd-marquee--running');
    });
  }

  /* ------------------------------------------------------------------- boot */

  function boot(root) {
    initReveals(root);
    initCounters(root);
    initMarquee(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  // Shopify theme editor: sections are injected after load.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });

  window.RDMotion = { boot: boot };
})();
